// src/components/games/crossroads/TutorialOverlay.tsx
// Interactive tutorial system for new players

import React, { useState, useEffect } from 'react';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonIcon,
  IonBadge,
} from '@ionic/react';
import {
  arrowForwardOutline,
  arrowBackOutline,
  closeOutline,
  checkmarkCircleOutline,
} from 'ionicons/icons';
import './TutorialOverlay.css';

export interface TutorialStep {
  title: string;
  description: string;
  highlightElement?: string; // CSS selector for element to highlight
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

interface TutorialOverlayProps {
  steps: TutorialStep[];
  onComplete: () => void;
  onSkip: () => void;
  autoStart?: boolean;
}

const TutorialOverlay: React.FC<TutorialOverlayProps> = ({
  steps,
  onComplete,
  onSkip,
  autoStart = true,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(autoStart);

  useEffect(() => {
    if (!isActive) return;

    // Highlight current element
    const step = steps[currentStep];
    if (step.highlightElement) {
      const element = document.querySelector(step.highlightElement);
      if (element) {
        element.classList.add('tutorial-highlight');
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    return () => {
      // Remove highlight from previous element
      document.querySelectorAll('.tutorial-highlight').forEach(el => {
        el.classList.remove('tutorial-highlight');
      });
    };
  }, [currentStep, isActive, steps]);

  if (!isActive || steps.length === 0) return null;

  const step = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    setIsActive(false);
    onSkip();
  };

  const handleComplete = () => {
    setIsActive(false);
    onComplete();
  };

  return (
    <>
      {/* Overlay backdrop */}
      <div className="tutorial-overlay-backdrop" />

      {/* Tutorial card */}
      <div className={`tutorial-card-container tutorial-${step.position || 'center'}`}>
        <IonCard className="tutorial-card">
          <IonCardHeader>
            <div className="tutorial-header">
              <IonCardTitle>{step.title}</IonCardTitle>
              <IonButton fill="clear" size="small" onClick={handleSkip}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </div>
            <IonBadge color="primary">
              Step {currentStep + 1} of {steps.length}
            </IonBadge>
          </IonCardHeader>
          <IonCardContent>
            <p className="tutorial-description">{step.description}</p>

            <div className="tutorial-actions">
              <IonButton
                fill="outline"
                onClick={handleBack}
                disabled={isFirstStep}
              >
                <IonIcon icon={arrowBackOutline} slot="start" />
                Back
              </IonButton>

              <IonButton fill="clear" onClick={handleSkip}>
                Skip Tutorial
              </IonButton>

              <IonButton onClick={handleNext} color="primary">
                {isLastStep ? (
                  <>
                    <IonIcon icon={checkmarkCircleOutline} slot="start" />
                    Finish
                  </>
                ) : (
                  <>
                    Next
                    <IonIcon icon={arrowForwardOutline} slot="end" />
                  </>
                )}
              </IonButton>
            </div>
          </IonCardContent>
        </IonCard>
      </div>
    </>
  );
};

export default TutorialOverlay;
